import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { api } from '@/services/api';
import type { AuthUser, Role } from '@/types';

interface AuthContextValue {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithOIDC: (code: string) => Promise<void>;
  logout: () => void;
  hasRole: (minRole: Role) => boolean;
  canAccessAgent: (tenantId: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ROLE_LEVELS: Record<Role, number> = {
  user: 0,
  reviewer: 1,
  admin: 2,
  super_admin: 3,
};

// Both tokens in localStorage so they survive page refresh.
const REFRESH_TOKEN_KEY = 'sentinel_refresh_token';
const ACCESS_TOKEN_KEY = 'sentinel_access_token';

// Module-level flags — survive React StrictMode remount, reset on real page nav.
let _restoreStarted = false;
let _isRestoring = false;  // blocks onUnauthorized during restore

export function AuthProvider({ children }: { children: ReactNode }) {
  // Initialize access token synchronously from localStorage so api.token
  // is set BEFORE any child component can render and fire API calls.
  const [accessToken, setAccessToken] = useState<string | null>(() => {
    const t = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (t) {
      api.setAccessToken(t);
    }
    return t;
  });
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearAuth = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    api.setAccessToken(null);
  }, []);

  const setAuth = useCallback((token: string, refreshToken: string) => {
    setAccessToken(token);
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    api.setAccessToken(token);
  }, []);

  // Restore session on mount.
  // Strategy: try the stored access token first via /me (no rotation).
  // Only fall back to refresh-token rotation when /me returns 401.
  useEffect(() => {
    if (_restoreStarted) return;
    _restoreStarted = true;
    _isRestoring = true;

    const restore = async () => {
      const storedAccess = localStorage.getItem(ACCESS_TOKEN_KEY);
      const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (!storedAccess && !storedRefresh) {
        _isRestoring = false;
        setIsLoading(false);
        return;
      }

      // 1) Try the stored access token — fast path, no rotation
      if (storedAccess) {
        try {
          const meRes = await fetch('/api/auth/me', {
            headers: { Authorization: `Bearer ${storedAccess}` },
          });
          if (meRes.ok) {
            const profile = await meRes.json();
            setAccessToken(storedAccess);
            setUser(profile);
            _isRestoring = false;
            // Wire up onUnauthorized NOW that restore is done
            api.setOnUnauthorized(() => {
              if (!_isRestoring) clearAuth();
            });
            setIsLoading(false);
            return;
          }
        } catch {
          // access token invalid, fall through to refresh
        }
      }

      // 2) Access token expired/invalid — rotate via refresh token
      if (storedRefresh) {
        try {
          const res = await fetch('/api/auth/refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: storedRefresh }),
          });
          if (!res.ok) throw new Error(`refresh returned ${res.status}`);
          const data = await res.json();
          setAuth(data.access_token, data.refresh_token);

          const meRes = await fetch('/api/auth/me', {
            headers: { Authorization: `Bearer ${data.access_token}` },
          });
          if (meRes.ok) {
            const profile = await meRes.json();
            setUser(profile);
            _isRestoring = false;
            api.setOnUnauthorized(() => {
              if (!_isRestoring) clearAuth();
            });
            setIsLoading(false);
            return;
          }
        } catch {
          // refresh failed
        }
      }

      // Both paths failed — clear tokens
      _isRestoring = false;
      clearAuth();
      api.setOnUnauthorized(() => {
        if (!_isRestoring) clearAuth();
      });
      setIsLoading(false);
    };

    restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Proactive token refresh (5 min before 60-min expiry = every 55 min)
  useEffect(() => {
    if (!accessToken) return;
    const interval = setInterval(async () => {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) return;
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!res.ok) throw new Error('refresh failed');
        const data = await res.json();
        setAuth(data.access_token, data.refresh_token);
      } catch {
        // Don't log out on transient failures — the next interval will retry.
        // Only clear auth if the refresh token is truly revoked (logged out elsewhere).
        console.warn('Token refresh failed, will retry on next interval');
      }
    }, 55 * 60 * 1000);
    return () => clearInterval(interval);
  }, [accessToken, setAuth, clearAuth]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const err = new Error(data.error || 'Login failed') as any;
      err.remaining_attempts = data.remaining_attempts;
      err.retry_after = data.retry_after;
      err.status = res.status;
      throw err;
    }
    const data = await res.json();
    setAuth(data.access_token, data.refresh_token);

    // Fetch full profile (login response doesn't include agent_tenant_ids)
    try {
      const meRes = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${data.access_token}` },
      });
      if (meRes.ok) {
        setUser(await meRes.json());
      } else {
        setUser(data.user);
      }
    } catch {
      setUser(data.user);
    }

    // Wire up onUnauthorized after login
    api.setOnUnauthorized(() => {
      if (!_isRestoring) clearAuth();
    });
  }, [setAuth, clearAuth]);

  const loginWithOIDC = useCallback(async (code: string) => {
    const data = await api.oidcExchange(code);
    setAuth(data.access_token, data.refresh_token);

    try {
      const meRes = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${data.access_token}` },
      });
      if (meRes.ok) {
        setUser(await meRes.json());
      } else {
        setUser(data.user);
      }
    } catch {
      setUser(data.user);
    }

    api.setOnUnauthorized(() => {
      if (!_isRestoring) clearAuth();
    });
  }, [setAuth, clearAuth]);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }).catch(() => {});
    }
    clearAuth();
  }, [clearAuth]);

  const hasRole = useCallback(
    (minRole: Role) => {
      if (!user) return false;
      return ROLE_LEVELS[user.role] >= ROLE_LEVELS[minRole];
    },
    [user],
  );

  const canAccessAgent = useCallback(
    (tenantId: string) => {
      if (!user) return false;
      if (user.role === 'super_admin') return true;
      if (!user.agent_tenant_ids) return true;
      return user.agent_tenant_ids.includes(tenantId);
    },
    [user],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        isAuthenticated: !!user,
        isLoading,
        login,
        loginWithOIDC,
        logout,
        hasRole,
        canAccessAgent,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
